import type { ReactNode } from 'react';

import type { Atom } from 'jotai';

import type { LeadFormState } from '@core/Atoms/Pdp/FormState.atom';
import type { Vehicle } from '@core/Entities/Vehicle/Vehicle.entity';
import { isBusinessHours } from '@core/Utils/isBusinessHours/isBusinessHours';
import { mapSelectedProductToLeadSubtype } from '@core/Utils/Leads/MapSelectedProductToLeadSubtype';
import { isInstaMotion } from '@core/Utils/Vehicle/IsInstaMotion';
import type { Calculation } from '@fincalc/Entities/Calculation.entity';
import { Call } from '@gds/Icons/Paths/Call';
import { CallBack } from '@gds/Icons/Paths/CallBack';
import { Email } from '@gds/Icons/Paths/Email';
import type { AllFlags } from '@growthBookExperimentation/Entities/FlagKeys.entities';
import type { StringsType } from '@lead-forms/Entities/LeadForm';

import type { PdPMessagesKeys } from '../Pdp.messages';
import type { LeadType } from './LeadCTAs.entity';

export type LeadCtaConfigFunc<T> = (props: {
  flags?: AllFlags;
  isEcom?: boolean;
  vehicle: Vehicle;
  isVsp?: boolean;
  calculation?: Calculation | null;
  isUk?: boolean;
}) => T;

export type LeadCtaRequiredFields = (keyof LeadFormState)[];

export type LeadCtaConfigValue = {
  labelId: (flags?: AllFlags) => PdPMessagesKeys;
  isActive: LeadCtaConfigFunc<boolean>;
  variant: LeadCtaConfigFunc<'outlined' | 'contained' | 'link'>;
  onClick?: LeadCtaConfigFunc<void>;
  successModalHeader: (strings: StringsType) => string | undefined;
  requiredFields?: () => LeadCtaRequiredFields;
  leadSubtype?: (
    values: LeadFormState,
    options: { calculation?: Calculation | null },
    flags?: AllFlags,
  ) => string;
  buttonIcon?: ReactNode;
  onErrorModalAction?: (strings: StringsType) => ReactNode;
  onSuccessModalAction?: (strings: StringsType) => ReactNode;
  submitHandlerAtom?: Atom<any>;
  successModalMessages?: (strings: StringsType) => {
    headLine?: string;
    description?: string;
  };
  loadingModalMessages?: (strings: StringsType) => {
    headLine?: string;
  };
  errorModalMessages?: (strings: StringsType) => {
    description?: string;
    headLine?: string;
  };
  showButtonText?: (props: {
    isVsp?: boolean;
    isEcom?: boolean;
    isSticky?: boolean;
    isUk?: boolean;
  }) => boolean | undefined;
};

export type LeadCtaConfig = Record<LeadType, LeadCtaConfigValue>;

export type LeadCtaConfigArray = Array<[LeadType, LeadCtaConfigValue]>;

export const leadCtasConfig = {
  'message-dealer': {
    labelId: () => 'messageDealerButton',
    isActive: ({ vehicle }) => !isInstaMotion(vehicle?.details?.dataSource),
    variant: ({ isEcom, isUk }) => (isEcom || !isUk ? 'outlined' : 'contained'),
    successModalHeader: strings => strings.messageDealerButton,
    leadSubtype: values =>
      values.reserveVehicleChkBox
        ? 'reservation'
        : values.preferredTime?.value && values.date
          ? 'request_a_viewing'
          : values.callBackInstantlyChkBox
            ? 'pending'
            : 'message',
    buttonIcon: <Email />,
    showButtonText: ({ isSticky, isEcom, isUk }) => !isSticky || (isSticky && !isEcom && isUk),
  },
  'message-dealer-finance': {
    labelId: () => 'messageDealerButton',
    isActive: () => false,
    variant: ({ isEcom }) => (isEcom ? 'outlined' : 'contained'),
    successModalHeader: strings => strings.messageDealerButton,
    leadSubtype: (values, { calculation }) =>
      values.reserveVehicleChkBox
        ? 'reservation'
        : values.preferredTime?.value && values.date
          ? 'request_a_viewing'
          : mapSelectedProductToLeadSubtype(calculation?.selectedProduct),
    buttonIcon: <Email />,
    showButtonText: ({ isSticky, isEcom }) => !isSticky || (isSticky && !isEcom),
  },
  'request-callback': {
    labelId: flags => (flags?.kaisaCallback ? 'scheduleCallBack' : 'requestCallbackButton'),
    isActive: ({ vehicle, flags }) =>
      !isInstaMotion(vehicle?.details?.dataSource) &&
      ((!isBusinessHours(vehicle.dealer?.openingTimes) || flags?.callbackCtaPublicHoliday) ??
        false),
    variant: ({ isEcom }) => (isEcom ? 'outlined' : 'contained'),
    successModalHeader: strings => strings.requestCallbackButton,
    leadSubtype: (_values, _options, flags) => (flags?.kaisaCallback ? 'pending' : 'callback'),
    buttonIcon: <CallBack />,
    showButtonText: ({ isSticky, isVsp, isEcom }) =>
      !isSticky || (isVsp && isSticky) || (isSticky && !isEcom),
  },
  'request-video': {
    labelId: () => 'requestVideoButton',
    isActive: ({ vehicle, flags }) =>
      !!(
        flags?.pdpRequestVideoCta &&
        !vehicle?.videos?.[0]?.link &&
        (vehicle?.pricing.package?.requestVideo ?? true)
      ),
    variant: ({ isEcom }) => (isEcom ? 'outlined' : 'contained'),
    successModalHeader: strings => strings.requestVideo,
    leadSubtype: () => 'video',
    showButtonText: () => true,
  },
  'request-images': {
    labelId: () => 'requestImagesButton',
    isActive: ({ vehicle, flags }) =>
      !!flags?.pdpRequestImagesCta &&
      (vehicle?.images.length === 0 || vehicle?.images[0].url.includes('coming-soon')),
    variant: ({ isEcom }) => (isEcom ? 'outlined' : 'contained'),
    successModalHeader: strings => strings.requestImages,
    leadSubtype: () => 'request_an_image',
    showButtonText: () => true,
  },
  'book-appointment': {
    labelId: () => 'bookAppointmentButton',
    isActive: ({ flags, vehicle }) =>
      !!flags?.leadEnrichmentBookAppointment && !!vehicle?.pricing?.package?.bookAppointment,
    variant: ({ isEcom }) => (isEcom ? 'outlined' : 'contained'),
    successModalHeader: strings => strings.bookAppointmentAccordionHeader,
    leadSubtype: () => 'request_a_viewing',
    showButtonText: () => true,
  },
  'leasing-online': {
    labelId: () => 'leasingOnlineButton',
    isActive: () => false,
    variant: ({ isEcom }) => (isEcom ? 'outlined' : 'contained'),
    successModalHeader: () => 'Message us',
    leadSubtype: () => 'leasing_online',
    showButtonText: () => true,
  },
  'call-dealer': {
    labelId: () => 'callDealerButton',
    isActive: ({ vehicle, flags }) =>
      !flags?.callbackCtaPublicHoliday && isBusinessHours(vehicle?.dealer?.openingTimes),
    variant: ({ isEcom, isUk }) => (!isUk && !isEcom ? 'contained' : 'outlined'),
    onClick: () => alert('Call dealer not implemented yet'),
    leadSubtype: () => 'call_dealer',
    successModalHeader: () => 'Call dealer not implemented yet',
    showButtonText: ({ isSticky, isVsp, isEcom }) =>
      !isSticky || (isVsp && isSticky) || (isSticky && !isEcom),
    buttonIcon: <Call />,
  },
} as const satisfies LeadCtaConfig;
